import React, { useMemo, useCallback, useState, useEffect } from "react";
import Dropdown from "../../components/Input/Dropdown";
import { useStateValue } from "../../util/stateProvider";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { formatValue } from "../../util/functions";
import Iconify from "../../components/iconify";
import { Box, Button, Popover, Stack, Typography } from "@mui/material";
import "../Table/EmployeeData.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { DeclarationFilterConfig } from "./DeclarationFilterConfig";
import { DeclarationSummaryFilterConfig } from "./DeclarationSummaryFilterConfig";
import FilterMaker from "../../components/FilterMaker/FilterMaker";
import TableV3 from "../../components/TableV3/TableV3";
import DeclarationListConfig from "./DeclarationListConfig";
import DeclarationSummaryConfig from "./DeclarationSummaryConfig";
import ManageDeclarations from "./ManageDeclaration/ManageDeclarations";
import { Declaration } from "typescript";
import formatToIndianCurrency from "../../util/function/currencyFormatter";
import V2Container from "../../components/Layout/V2Container";

const Declarations = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const policyIdFromState = location.state?.policyId;
    const [{ leadInfo }] = useStateValue();
    const { policyDto } = useSelector((state: RootState) => state.home);
    const { tableData, extras } = useSelector(
        (state: RootState) => state.table["default"]
    );

    const uuid = sessionStorage.getItem("uuid") || leadInfo?.uuid;

    const policyNumberOptions = useMemo(
        () =>
            policyDto?.d_home_policies
                ?.filter(
                    (policy: { insuranceCategory: string }) =>
                        policy.insuranceCategory === "Marine"
                )
                ?.map((policy: { policyNumber: string; pid: string }) => ({
                    id: policy?.pid,
                    name: policy?.policyNumber,
                })) || [],
        [policyDto]
    );

    const [selectedPolicyNumber, setSelectedPolicyNumber] = useState<
        string | null
    >(null);
    const [selectedTable, setSelectedTable] = useState<string>("declaration");
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [openManageDeclarations, setOpenManageDeclarations] = useState<
        0 | 1 | 2
    >(0);
    const [editId, setEditId] = useState<string | null>(null);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const [declarationFilterConfig, setDeclarationFilterConfig] = useState(
        DeclarationFilterConfig
    );

    const [declarationSummaryFilterConfig, setDeclarationSummaryFilterConfig] =
        useState(DeclarationSummaryFilterConfig);

    const [selectedConfig, setSelectedConfig] = useState(
        selectedTable === "declaration"
            ? declarationFilterConfig
            : declarationSummaryFilterConfig
    );

    const [selectedTableConfig, setSelectedTableConfig] = useState(
        selectedTable === "declaration"
            ? DeclarationListConfig
            : DeclarationSummaryConfig
    );

    const functions = {
        edit: (row: { id: string }) => {
            setEditId(row.id);
            setOpenManageDeclarations(1);
        },
    };

    // useEffect(() => {
    //     if (policyDto?.d_home_policies?.length && !selectedPolicyNumber) {
    //         setSelectedPolicyNumber(policyDto.d_home_policies[0].pid);
    //     }
    // }, [policyDto, selectedPolicyNumber]);

    useEffect(() => {
        let tempPolicyList = policyDto?.d_home_policies
            ?.filter(
                (policy: { insuranceCategory: string }) =>
                    policy.insuranceCategory === "Marine"
            )
            ?.map((policy: { policyNumber: string; pid: string }) => ({
                id: policy?.pid,
                name: policy?.policyNumber,
            }));

        if (policyIdFromState && tempPolicyList.length) {
            // @ts-ignore
            setSelectedPolicyNumber(Number(policyIdFromState||"0"));
        } else if (tempPolicyList.length && !selectedPolicyNumber) {
            setSelectedPolicyNumber(tempPolicyList[0].id);
        }
    }, [policyDto, selectedPolicyNumber, policyIdFromState]);

    useEffect(() => {
        if (selectedPolicyNumber && uuid) {
            setDeclarationFilterConfig({
                ...DeclarationFilterConfig,
                url: selectedPolicyNumber
                    ? `v1/crm/policy/declaration/${uuid}/${selectedPolicyNumber}`
                    : DeclarationFilterConfig.url,
                statusCountUrl: selectedPolicyNumber
                    ? `v1/crm/policy/declaration/${uuid}/${selectedPolicyNumber}/filter/count`
                    : DeclarationFilterConfig.statusCountUrl,
            });

            setDeclarationSummaryFilterConfig({
                ...DeclarationSummaryFilterConfig,
                url: selectedPolicyNumber
                    ? `v1/crm/policy/declaration/${uuid}/${selectedPolicyNumber}`
                    : DeclarationSummaryFilterConfig.url,
            });
        }
    }, [selectedPolicyNumber, uuid]);

    useEffect(() => {
        setSelectedConfig(
            selectedTable === "declaration"
                ? declarationFilterConfig
                : declarationSummaryFilterConfig
        );

        setSelectedTableConfig(
            selectedTable === "declaration"
                ? DeclarationListConfig
                : DeclarationSummaryConfig
        );
    }, [
        selectedTable,
        declarationFilterConfig,
        declarationSummaryFilterConfig,
    ]);

    const handleOpenPopover = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
        },
        []
    );

    const handleClosePopover = useCallback(() => setAnchorEl(null), []);

    const handleOptionClick = (type: "singleEntry" | "bulkEntry") => {
        setOpenManageDeclarations(type === "singleEntry" ? 1 : 2);
        setEditId(null);
        setAnchorEl(null);
    };

    const handleRefresh = useCallback(() => {
        setRefreshKey((prev) => prev + 1);
    }, []);

    return (
        <V2Container
            onBack={() => {
                navigate(-1);
            }}
            heading="Declarations"
            topSection={
                <Stack
                    // sx={{
                    //     gap: 15,
                    //     display: "flex",
                    //     flexDirection: "row",
                    //     justifyContent: "space-between",
                    //     alignItems: "center",
                    //     marginBottom: 20,
                    // }}
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    sx={{
                        width: "100%",
                    }}
                >
                    {/* Float balance display */}
                    <Box
                        sx={{
                            padding: "16px",
                            backgroundColor: "#fff",
                            borderRadius: "20px",
                        }}
                    >
                        <Typography variant="h5">
                            {formatToIndianCurrency(
                                Math.floor(extras?.remainingSumInsured || 0),
                                true
                            )}
                        </Typography>
                        <Typography variant="body3">
                            Remaining Sum Insured
                        </Typography>
                    </Box>

                    <div>
                        <Button
                            // loading={0}
                            id="addMemberId"
                            onMouseEnter={handleOpenPopover}
                            size="small"
                            sx={(theme) => ({
                                backgroundColor: theme.palette.brand?.main,
                                color: theme.palette.common.white,
                            })}
                        >
                            <Iconify
                                icon="ion:plus"
                                height={"16px"}
                                width={"16px"}
                            />
                            &nbsp; Add Declaration
                        </Button>

                        <Popover
                            id="manageMemberPopup"
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClosePopover}
                            disableScrollLock={true}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            slotProps={{
                                paper: {
                                    sx: {
                                        borderRadius: "12px",
                                        overflow: "visible",
                                    },
                                },
                            }}
                        >
                            <Stack
                                className="addEmployee"
                                gap={"8px"}
                                alignContent={"center"}
                                direction={"column"}
                                sx={{
                                    padding: "16px",
                                    borderRadius: "12px !important",
                                }}
                                onMouseLeave={handleClosePopover}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        height: "65px",
                                        width: "206px",
                                        top: "0",
                                        right: "0",
                                        transform: "translateY(-100%)",
                                    }}
                                />
                                <Stack
                                    direction={"row"}
                                    gap={"4px"}
                                    className="addEmployee_option"
                                    onClick={() =>
                                        handleOptionClick("singleEntry")
                                    }
                                    sx={{
                                        padding: "8px 4px",
                                        "&:hover": {
                                            backgroundColor: (theme) =>
                                                theme.palette.grey[100],
                                            cursor: "pointer",
                                        },
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: "24px",
                                            width: "24px",
                                        }}
                                    >
                                        <Iconify
                                            icon="material-symbols:add"
                                            width="24px"
                                            height="24px"
                                        />
                                    </Box>
                                    <Typography variant="body3">
                                        Add single Declaration
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction={"row"}
                                    alignContent={"center"}
                                    gap={"4px"}
                                    className="addEmployee_option"
                                    onClick={() =>
                                        handleOptionClick("bulkEntry")
                                    }
                                    sx={{
                                        padding: "8px 4px",
                                        "&:hover": {
                                            backgroundColor: (theme) =>
                                                theme.palette.grey[100],
                                            cursor: "pointer",
                                        },
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: "24px",
                                            width: "24px",
                                        }}
                                    >
                                        <Iconify
                                            icon="material-symbols:group-add"
                                            width="24px"
                                            height="24px"
                                            sx={{ marginRight: "4px" }}
                                        />
                                    </Box>
                                    <Typography variant="body3">
                                        Add multiple Declaration
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Popover>
                    </div>
                </Stack>
            }
            headingRight={
                <div>
                    <div className="top-metadata-container">
                        {policyNumberOptions?.length > 1 &&
                            selectedPolicyNumber && (
                                <Dropdown
                                    size={"small"}
                                    items={policyNumberOptions}
                                    defaultVal={selectedPolicyNumber}
                                    change={setSelectedPolicyNumber}
                                    className="inverted topDropdown"
                                />
                            )}
                        <Dropdown
                            size={"small"}
                            items={[
                                {
                                    id: "declaration",
                                    name: "Declarations",
                                },
                                {
                                    id: "declarationSummary",
                                    name: "Declaration Summary",
                                },
                            ]}
                            defaultVal={selectedTable}
                            change={setSelectedTable}
                            className="inverted topDropdown"
                        />
                    </div>
                </div>
            }
        >
            <Box sx={{ width: "100%" }}>
                <FilterMaker config={selectedConfig} refreshKey={refreshKey}>
                    <TableV3
                        config={selectedTableConfig}
                        data={tableData as any}
                        functions={functions}
                    />
                </FilterMaker>

                {selectedPolicyNumber ? (
                    openManageDeclarations === 1 ? (
                        editId ? (
                            <ManageDeclarations
                                close={() => {
                                    setOpenManageDeclarations(0);
                                }}
                                uuid={uuid}
                                pid={selectedPolicyNumber}
                                caseType="Edit"
                                id={editId}
                                refresh={handleRefresh}
                            />
                        ) : (
                            <ManageDeclarations
                                close={() => {
                                    setOpenManageDeclarations(0);
                                }}
                                uuid={uuid}
                                pid={selectedPolicyNumber}
                                caseType="AddSingle"
                                refresh={handleRefresh}
                            />
                        )
                    ) : openManageDeclarations === 2 ? (
                        <ManageDeclarations
                            close={() => {
                                setOpenManageDeclarations(0);
                            }}
                            uuid={uuid}
                            pid={selectedPolicyNumber}
                            caseType="UpsertMultiple"
                            refresh={handleRefresh}
                        />
                    ) : null
                ) : null}
            </Box>
        </V2Container>
    );
};

export default Declarations;
